/**
 * Shared MathJax-delimiter helpers for SingleSelectQuestion feedback.
 *
 * Feedback markup can embed LaTeX in MathJax delimiters: display `$$…$$` and
 * inline `$…$`. Two callers reason about that markup:
 *   - RadioButton decides whether to render feedback as an equation
 *     (`containsMath`);
 *   - the parent's screen-reader announcement replaces each math expression
 *     with a short label instead of silently dropping it (`stripMathToLabel`).
 *
 * The naive `/\$\$.*?\$\$|\$.*?\$/g` this replaces has two defects the reviewer
 * flagged:
 *   1. `.` never crosses newlines, so multiline display math is missed;
 *   2. it mangles prose that uses `$` as currency — "$5 and $10" is captured as
 *      the single match "$5 and $".
 *
 * Fixes here:
 *   - display math matches across newlines (`[\s\S]`);
 *   - inline math uses the common markdown-it heuristic — the opening `$` is
 *     not escaped, not part of `$$`, and is immediately followed by a non-space;
 *     the body has no `$`/newline and ends in a non-space; the closing `$` is
 *     not followed by a digit. That keeps real inline math ($x^2$, $a + b$,
 *     $\alpha$) while leaving spaced currency amounts untouched.
 *
 * Lookbehind is deliberately avoided (older Safari throws a SyntaxError parsing
 * a lookbehind literal, which would break the whole bundle); the char before an
 * inline `$` is captured in group 1 and preserved on replace instead.
 */

// Display first so the inline pass never splits a `$$` delimiter.
const DISPLAY_MATH = /\$\$[\s\S]+?\$\$/g;
const INLINE_MATH = /(^|[^\\$])\$(?!\s)([^$\n]*[^\s$])\$(?!\d)/g;

// Non-global copies for stateless detection (avoids `/g` lastIndex carry-over).
const DISPLAY_MATH_TEST = new RegExp(DISPLAY_MATH.source);
const INLINE_MATH_TEST = new RegExp(INLINE_MATH.source);

/** True when the text contains display or inline MathJax math. */
export const containsMath = (text: string | undefined): boolean => {
  if (!text || typeof text !== "string") return false;
  return DISPLAY_MATH_TEST.test(text) || INLINE_MATH_TEST.test(text);
};

/** Replace every math expression in `markup` with a spoken-word `label`. */
export const stripMathToLabel = (markup: string, label: string): string =>
  markup
    .replace(DISPLAY_MATH, ` ${label} `)
    .replace(INLINE_MATH, (_match, before) => `${before} ${label} `);
